<template>
  <form name="form" v-on:submit.prevent="submit" v-on:change="changeHandler">
    <div class="card-body">
      <div class="col-lg-6">
        <toggleswitch
          :labels="true"
          name="in_storage"
          label="Is the vehicle in Storage?"
          helper="Is the third party's vehicle currently in a storage facility?"
          v-model="form.in_storage"
        ></toggleswitch>

        <div v-if="form.in_storage">
          <toggleswitch
            :labels="true"
            name="company_name"
            label="Are you storing the vehicle yourself?"
            helper="Is the third party's vehicle being self stored?"
            v-model="form.self_storage"
          ></toggleswitch>

          <div
            class="form-group"
            :class="{ 'has-error': form.errors.has('started_at') }"
          >
            <div class="col-lg-4">
              <label for="storage_date" class="control-label"
                >Storage Start Date:</label
              >
              <p class="help-block">
                Please enter the date that the storage started
              </p>
            </div>
            <div class="col-lg-8">
              <datepicker
                format="dd/MM/yyyy"
                v-bind:value="form.started_at"
                input-class="form-control"
                v-on:selected="setStartDate"
                placeholder="dd/mm/yyyy"
              ></datepicker>
              <span
                class="text-danger"
                v-text="form.errors.get('started_at')"
                v-if="form.errors.has('started_at')"
              ></span>
            </div>
          </div>

          <inputbox
            name="storage_rate"
            label="Storage Rate"
            v-model="storageRate"
            helper="Please enter the daily charge for storing the vehicle"
          >
            <div slot="leftAddon">&pound;</div>
          </inputbox>

          <div
            class="form-group"
            :class="{ 'has-error': form.errors.has('ended_at') }"
          >
            <div class="col-lg-4">
              <label for="storage_date" class="control-label"
                >Storage End Date:</label
              >
              <p class="help-block">Please enter the date the storage ended</p>
            </div>
            <div class="col-lg-8">
              <datepicker
                format="dd/MM/yyyy"
                v-bind:value="form.ended_at"
                input-class="form-control"
                v-on:updated="setEndDate"
                v-on:selected="setEndDate"
                placeholder="dd/mm/yyyy"
              ></datepicker>
              <span
                class="text-danger"
                v-text="form.errors.get('ended_at')"
                v-if="form.errors.has('ended_at')"
              ></span>
            </div>
          </div>

          <inputbox
            v-model="form.telephone"
            name="telephone"
            label="Telephone"
            helper="Please enter the telephone number of the storage company"
            :show-error="form.errors.has('telephone')"
          ></inputbox>
        </div>

        <address-lookup
          :address="address"
          v-on:address="setAddress"
        ></address-lookup>
      </div>
      <div class="col-lg-6">
        <toggleswitch
          :labels="true"
          name="recovered"
          label="Was this vehicle recovered?"
          helper="Was a recovery company called out to recover the vehicle?"
          v-model="recovery.recovered"
        ></toggleswitch>

        <div v-show="recovery.recovered">
          <div class="form-group">
            <div class="col-lg-4">
              <label for="recovered_at" class="control-label"
                >Date Recovered</label
              >
              <p class="help-block">
                Please enter the date the vehicle was recovered
              </p>
            </div>
            <div class="col-lg-8">
              <datepicker
                format="dd/MM/yyyy"
                :value="recovery.recovered_at"
                input-class="form-control"
                v-on:selected="setDateRecovered"
                placeholder="dd/mm/yyyy"
              ></datepicker>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-8 offset-lg-4">
              <div class="alert alert-info">
                <p class="mb-0">Where was the vehicle picked up from?</p>
              </div>
            </div>
          </div>

          <address-lookup
            :address="pickupLocation"
            v-on:address="setPickupAddress"
          ></address-lookup>

          <inputbox
            name="recovery_charge"
            label="Recovery Charge"
            v-model="recoveryCharge"
            helper="Please enter the charge for recovering the vehicle"
          >
            <div slot="leftAddon">&pound;</div>
          </inputbox>
        </div>
      </div>
    </div>
    <div class="card-footer text-right">
      <button
        class="btn btn-primary btn-raised"
        name="cancel"
        v-on:click.prevent="cancel"
      >
        <i class="icon icon-cancel"></i> Back
      </button>
      <button
        class="btn btn-primary btn-raised"
        name="save"
        v-on:click.prevent="submit"
      >
        <i class="icon icon-save"></i> Save
      </button>
    </div>
  </form>
</template>
<script>
import debounce from 'lodash/debounce';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },

    thirdParty: {
      type: String
    },

    mode: {
      type: String
    }
  },

  data() {
    return {
      vehicle: null,
      companies: [],
      form: new window.Form({
        in_storage: false,
        self_storage: false,
        recovered: false,
        address_line_one: null,
        address_line_two: null,
        city: null,
        postcode: null,
        company_name: null,
        started_at: null,
        ended_at: null,
        telephone: null,
        storage_rate: 0
      }),
      recovery: new window.Form({
        recovered: false,
        company_id: null,
        recovered_at: null,
        address_line_one: null,
        address_line_two: null,
        city: null,
        postcode: null,
        charge: 0
      }),
      data_entered: false
    };
  },

  mounted() {
    this.mount();
  },

  computed: {
    address() {
      return {
        address_line_one: this.form.address_line_one,
        address_line_two: this.form.address_line_two,
        city: this.form.city,
        postcode: this.form.postcode
      };
    },

    pickupLocation() {
      return {
        address_line_one: this.recovery.address_line_one,
        address_line_two: this.recovery.address_line_two,
        city: this.recovery.city,
        postcode: this.recovery.postcode
      };
    },

    recoveryCharge: {
      get() {
        return this.penceToPounds(this.recovery.charge);
      },
      set: debounce(function(newValue) {
        if (newValue > 0) {
          this.recovery.charge = parseInt(this.poundsToPence(newValue));
          return;
        }

        this.recovery.charge = 0;
      }, 500)
    },

    storageRate: {
      get() {
        return this.penceToPounds(this.form.storage_rate);
      },
      set: debounce(function(newValue) {
        if (newValue > 0) {
          this.form.storage_rate = parseInt(this.poundsToPence(newValue));
          return;
        }

        this.form.storage_rate = 0;
      }, 500)
    }
  },

  methods: {
    mount() {
      window.Ajax.get(
        `/incidents/${this.incident}/third-parties/${this.thirdParty}`
      )
        .then(response => {
          this.third_party = response.data;

          window.Ajax.get(
            `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle`
          )
            .then(response => {
              this.vehicle = response.data.identifier;

              window.Ajax.get(
                `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle/${this.vehicle}/location`
              )
                .then(response => {
                  this.setData(response.data);
                })
                .catch(error => {});

              window.Ajax.get(
                `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle/${this.vehicle}/recovery`
              )
                .then(response => {
                  this.setRecoveryData(response.data);
                })
                .catch(error => {});
            })
            .catch(error => {});
        })
        .catch(error => {});

      window.Ajax.get('/companies?type=storage_and_recovery')
        .then(response => {
          this.companies = response.data;
        })
        .catch(response => {});
    },

    copyClientAddress() {
      this.setAddress(this.client);
      window.toastify.success(
        'Complete',
        "We've copied those details for you."
      );
    },

    setData(data) {
      this.form.identifier = data.identifier;
      this.form.in_storage = data.in_storage;
      this.form.storage_rate = data.storage_rate ? data.storage_rate : 0;
      this.form.self_storage = data.self_storage;
      this.form.address_line_one = data.address_line_one;
      this.form.address_line_two = data.address_line_two;
      this.form.city = data.city;
      this.form.postcode = data.postcode;
      this.form.company_name = data.company_name;
      this.form.started_at = data.started_at ? data.started_at : null;
      this.form.ended_at = data.ended_at ? data.ended_at : null;
      this.form.telephone = data.telephone;
    },

    setRecoveryData(data) {
      this.recovery.recovered = data.recovered;
      this.recovery.address_line_one = data.address_line_one;
      this.recovery.address_line_two = data.address_line_two;
      this.recovery.company_id = data.company_id;
      this.recovery.city = data.city;
      this.recovery.postcode = data.postcode;
      this.recovery.recovered_at = data.recovered_at
        ? window.DateManager.format(data.recovered_at)
        : null;
      this.recovery.charge = data.charge ? data.charge : 0;
    },

    setAddress(address) {
      this.form.address_line_one = address.address_line_one;
      this.form.address_line_two = address.address_line_two;
      this.form.city = address.city;
      this.form.postcode = address.postcode;
    },

    setPickupAddress(address) {
      this.recovery.address_line_one = address.address_line_one;
      this.recovery.address_line_two = address.address_line_two;
      this.recovery.city = address.city;
      this.recovery.postcode = address.postcode;
    },

    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }
      this.$emit('unsaved-data', this.data_entered);
    },

    submit() {
      window.toastify.info('Saving', "We're saving those details for you.");

      this.form
        .post(
          `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle/${this.vehicle}/location`
        )
        .then(response => {
          this.recovery
            .post(
              `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle/${this.vehicle}/recovery`
            )
            .then(response => {
              window.toastify.success(
                'Saved',
                "We've updated those details for you."
              );
              // reset monitoring of form changes
              this.data_entered = false;
              this.$emit('unsaved-data', this.data_entered);
            })
            .catch(response => {
              window.toastify.error(
                'Error',
                'Sorry, there was an error saving the vehicle location.'
              );
            });
        })
        .catch(response => {
          window.toastify.error(
            'Error',
            'Sorry, there was an error saving the vehicle location.'
          );
        });
    },

    cancel() {
      window
        .swal({
          title: 'Are you sure?',
          text: 'You will lose any unsaved changes',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes'
        })
        .then(confirmed => {
          if (confirmed) {
            this.$emit('close');
          }
          this.$router.back();
        })
        .catch(error => {});
    },

    setStartDate(value) {
      this.form.started_at = value;
    },

    setDateRecovered(value) {
      this.recovery.recovered_at = value;
    },

    setEndDate(value) {
      this.form.ended_at = value;
    }
  },

  watch: {
    $route(to, from) {
      this.mount();
    }
  }
};
</script>
